<script setup lang="ts">
import { PV } from "@/widgets";
import { watchEffect } from "vue";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormExt, FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = string;
interface PropsType extends BaseProps<ModelType> {
    formId?: string;
    placeholder?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
});

const slots = defineSlots<{
    icon?: () => unknown;
}>();

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

watchEffect(() => {
    if (model.value === "" || model.value === null) {
        model.value = undefined;
    }
});
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.InputText v-if="state.hasValue" v-model="model" class="rns-input-static" disabled fluid />
                <PV.InputText v-else class="rns-input-placeholder" disabled fluid :value="props.placeholder" />
                <FormExt :actions="actions" :state="state" />
            </PV.IconField>
        </template>
        <template v-else>
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.InputText
                    v-model.trim="model"
                    :disabled="props.disabled"
                    fluid
                    :form="props.formId"
                    :inputId="state.id"
                    :invalid="state.hasError"
                    :placeholder="props.placeholder ?? `Masukkan ${label}`"
                    :required="props.required"
                    @change="actions.onValidate"
                    @keyup.enter.prevent="actions.onSubmit"
                />
                <Buttons.ExtReset v-if="state.clearable && state.hasValue" @click="actions.onReset" />
            </PV.IconField>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-static,
.rns-input-placeholder {
    @apply bg-transparent! shadow-none! px-1! pt-2.25!;
    @apply rns-g-static-input;
}

.rns-input-static {
    @apply text-gray-700!;
}

.rns-input-placeholder {
    @apply text-gray-500!;
}
</style>
